import { useRef, useEffect } from "react";
import QRCodeStyling from "qr-code-styling";
import { PRESET_LOGOS } from "@/lib/presetLogos";

function getLogoUrl(style) {
  if (!style) return undefined;
  if (style.logo_base64) return style.logo_base64;
  if (style.logo_preset) {
    const preset = PRESET_LOGOS.find((p) => p.id === style.logo_preset);
    return preset?.url || undefined;
  }
  return undefined;
}

export default function StyledQRCode() {
  // Use the useStyledQR hook instead
  return null;
}

function buildOptions(data, style, size) {
  const logoUrl = getLogoUrl(style);
  const opts = {
    width: size,
    height: size,
    data: data || "https://qrhub.app",
    type: "svg",
    dotsOptions: {
      color: style?.fg_color || "#09090B",
      type: style?.dot_type || "square",
    },
    cornersSquareOptions: {
      type: style?.corner_square_type || "square",
      color: style?.fg_color || "#09090B",
    },
    cornersDotOptions: {
      type: style?.corner_dot_type || "square",
      color: style?.fg_color || "#09090B",
    },
    backgroundOptions: {
      color: style?.bg_color || "#FFFFFF",
    },
    imageOptions: {
      crossOrigin: "anonymous",
      margin: 4,
      imageSize: 0.35,
    },
    qrOptions: {
      errorCorrectionLevel: "H",
    },
  };
  if (logoUrl) opts.image = logoUrl;
  return opts;
}

const DOWNLOAD_SIZE = 2048;

function buildStarString(rating, maxStars = 5) {
  const safeMax = Number.isFinite(maxStars) ? Math.max(1, Math.min(10, Math.round(maxStars))) : 5;
  const safeRating = Number.isFinite(rating) ? Math.max(0, Math.min(safeMax, Math.round(rating))) : safeMax;
  return "★".repeat(safeRating) + "☆".repeat(safeMax - safeRating);
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function blobToImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
}

async function downloadDecoratedPNG({ data, style, filename }) {
  const qr = new QRCodeStyling(buildOptions(data, style, DOWNLOAD_SIZE));
  const qrBlob = await qr.getRawData("png");
  const qrImg = await blobToImage(qrBlob);

  const bottomText = (style?.bottom_text || "").trim();
  const showStars = Boolean(style?.show_review_stars);
  const rating = Number.isFinite(style?.review_rating) ? style.review_rating : 5;
  const fg = style?.fg_color || "#09090B";
  const bg = style?.bg_color || "#FFFFFF";

  const paddingY = Math.round(DOWNLOAD_SIZE * 0.07);
  const starsFontSize = Math.round(DOWNLOAD_SIZE * 0.06);
  const textFontSize = Math.round(DOWNLOAD_SIZE * 0.045);
  const lineGap = Math.round(DOWNLOAD_SIZE * 0.03);

  const extra =
    (showStars ? starsFontSize + lineGap : 0) +
    (bottomText ? textFontSize + lineGap : 0) +
    (showStars || bottomText ? paddingY : 0);

  const canvas = document.createElement("canvas");
  canvas.width = DOWNLOAD_SIZE;
  canvas.height = DOWNLOAD_SIZE + extra;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(qrImg, 0, 0, DOWNLOAD_SIZE, DOWNLOAD_SIZE);

  let y = DOWNLOAD_SIZE + paddingY;
  ctx.fillStyle = fg;
  ctx.textAlign = "center";

  if (showStars) {
    ctx.font = `800 ${starsFontSize}px system-ui, -apple-system, Segoe UI, Roboto, Arial`;
    ctx.textBaseline = "top";
    ctx.fillText(buildStarString(rating, 5), DOWNLOAD_SIZE / 2, y);
    y += starsFontSize + lineGap;
  }

  if (bottomText) {
    ctx.font = `800 ${textFontSize}px system-ui, -apple-system, Segoe UI, Roboto, Arial`;
    ctx.textBaseline = "top";
    ctx.fillText(bottomText, DOWNLOAD_SIZE / 2, y);
  }

  const outBlob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  if (outBlob) downloadBlob(outBlob, `${filename || "qr-code"}.png`);
}

async function downloadDecoratedSVG({ data, style, filename }) {
  const qr = new QRCodeStyling(buildOptions(data, style, DOWNLOAD_SIZE));
  const qrBlob = await qr.getRawData("svg");
  const qrSvgText = await qrBlob.text();

  const bottomText = (style?.bottom_text || "").trim();
  const showStars = Boolean(style?.show_review_stars);
  const rating = Number.isFinite(style?.review_rating) ? style.review_rating : 5;
  const fg = style?.fg_color || "#09090B";
  const bg = style?.bg_color || "#FFFFFF";

  const paddingY = Math.round(DOWNLOAD_SIZE * 0.07);
  const starsFontSize = Math.round(DOWNLOAD_SIZE * 0.06);
  const textFontSize = Math.round(DOWNLOAD_SIZE * 0.045);
  const lineGap = Math.round(DOWNLOAD_SIZE * 0.03);

  const extra =
    (showStars ? starsFontSize + lineGap : 0) +
    (bottomText ? textFontSize + lineGap : 0) +
    (showStars || bottomText ? paddingY : 0);

  const totalHeight = DOWNLOAD_SIZE + extra;
  const encodedQr = btoa(unescape(encodeURIComponent(qrSvgText)));
  const qrDataUri = `data:image/svg+xml;base64,${encodedQr}`;

  let y = DOWNLOAD_SIZE + paddingY;
  let overlay = "";
  if (showStars) {
    overlay += `<text x="${DOWNLOAD_SIZE / 2}" y="${y + starsFontSize}" fill="${fg}" font-family="system-ui, -apple-system, Segoe UI, Roboto, Arial" font-size="${starsFontSize}" font-weight="800" text-anchor="middle">${buildStarString(rating, 5)}</text>`;
    y += starsFontSize + lineGap;
  }
  if (bottomText) {
    overlay += `<text x="${DOWNLOAD_SIZE / 2}" y="${y + textFontSize}" fill="${fg}" font-family="system-ui, -apple-system, Segoe UI, Roboto, Arial" font-size="${textFontSize}" font-weight="800" text-anchor="middle">${bottomText.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</text>`;
  }

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${DOWNLOAD_SIZE}" height="${totalHeight}" viewBox="0 0 ${DOWNLOAD_SIZE} ${totalHeight}">
<rect x="0" y="0" width="${DOWNLOAD_SIZE}" height="${totalHeight}" fill="${bg}"/>
<image href="${qrDataUri}" x="0" y="0" width="${DOWNLOAD_SIZE}" height="${DOWNLOAD_SIZE}"/>
${overlay}
</svg>`;

  const outBlob = new Blob([svg], { type: "image/svg+xml" });
  downloadBlob(outBlob, `${filename || "qr-code"}.svg`);
}

export function useStyledQR({ data, style, size = 200 }) {
  const ref = useRef(null);
  const qrRef = useRef(null);
  const latestData = useRef(data);
  const latestStyle = useRef(style);

  latestData.current = data;
  latestStyle.current = style;

  useEffect(() => {
    if (!ref.current) return;
    const opts = buildOptions(data, style, size);

    // Always recreate QR instance to ensure clean state (fixes logo removal bug)
    const qr = new QRCodeStyling(opts);
    qrRef.current = qr;
    ref.current.innerHTML = "";
    qr.append(ref.current);
  }, [data, style, size]);

  const downloadPNG = (filename) => {
    const s = latestStyle.current;
    const hasBottomText = Boolean((s?.bottom_text || "").trim());
    const hasStars = Boolean(s?.show_review_stars);
    if (hasBottomText || hasStars) {
      downloadDecoratedPNG({ data: latestData.current, style: s, filename });
      return;
    }
    const hiRes = new QRCodeStyling(buildOptions(latestData.current, s, DOWNLOAD_SIZE));
    hiRes.download({ name: filename || "qr-code", extension: "png" });
  };

  const downloadSVG = (filename) => {
    const s = latestStyle.current;
    const hasBottomText = Boolean((s?.bottom_text || "").trim());
    const hasStars = Boolean(s?.show_review_stars);
    if (hasBottomText || hasStars) {
      downloadDecoratedSVG({ data: latestData.current, style: s, filename });
      return;
    }
    const hiRes = new QRCodeStyling(buildOptions(latestData.current, s, DOWNLOAD_SIZE));
    hiRes.download({ name: filename || "qr-code", extension: "svg" });
  };

  return { ref, downloadPNG, downloadSVG };
}
